import { parseCompound, type FormulaError } from './chemicalEquation';

export interface IonicSpecies {
  coefficient: number;
  formula: string;
  charge: number;
  state: 'aq' | 'g' | 'l' | 's' | null;
  atoms: Record<string, number>;
}

export interface IonicEquation {
  left: IonicSpecies[];
  right: IonicSpecies[];
}

export interface IonicEquationAnalysis {
  equation: IonicEquation;
  atomBalance: Array<{ symbol: string; left: number; right: number; balanced: boolean }>;
  leftCharge: number;
  rightCharge: number;
  atomsBalanced: boolean;
  chargeBalanced: boolean;
  spectatorSpecies: IonicSpecies[];
  netEquation: IonicEquation;
}

export interface IonicParseResult {
  analysis: IonicEquationAnalysis | null;
  error: FormulaError | null;
}

const STATE_PATTERN = /\((aq|s|l|g)\)$/i;

function speciesKey(species: IonicSpecies): string {
  return `${species.formula}|${species.charge}|${species.state ?? ''}`;
}

function parseCharge(input: string): {
  formula: string;
  charge: number;
  error: string | null;
} {
  const caret = input.match(/\^(\d+)([+-])$/);
  if (caret) {
    const magnitude = Number(caret[1]);
    if (magnitude < 1)
      return { formula: '', charge: 0, error: 'Charge magnitude must be at least 1.' };
    return {
      formula: input.slice(0, caret.index),
      charge: caret[2] === '+' ? magnitude : -magnitude,
      error: null,
    };
  }
  const single = input.match(/([+-])$/);
  if (single) {
    const formula = input.slice(0, -1);
    if (/\^|[+-]/.test(formula)) {
      return {
        formula: '',
        charge: 0,
        error: 'Write multi-charge ions with a caret, for example Fe^3+ or SO4^2-.',
      };
    }
    return {
      formula,
      charge: single[1] === '+' ? 1 : -1,
      error: null,
    };
  }
  if (/\^|[+-]/.test(input)) {
    return {
      formula: '',
      charge: 0,
      error: 'Write multi-charge ions with a caret, for example Fe^3+ or SO4^2-.',
    };
  }
  return { formula: input, charge: 0, error: null };
}

export function parseIonicSpecies(input: string): {
  species: IonicSpecies | null;
  error: FormulaError | null;
} {
  const trimmed = input.trim();
  const coefficientMatch = trimmed.match(/^(\d+)\s*/);
  const coefficient = coefficientMatch ? Number(coefficientMatch[1]) : 1;
  if (coefficient < 1) {
    return { species: null, error: { index: 0, message: 'A coefficient must be at least 1.' } };
  }
  let core = trimmed.slice(coefficientMatch?.[0].length ?? 0).replaceAll('−', '-');
  const stateMatch = core.match(STATE_PATTERN);
  const state = (stateMatch?.[1]?.toLowerCase() as IonicSpecies['state']) ?? null;
  if (stateMatch) core = core.slice(0, stateMatch.index);
  const charge = parseCharge(core);
  if (charge.error) {
    return {
      species: null,
      error: { index: coefficientMatch?.[0].length ?? 0, message: charge.error },
    };
  }
  if (!charge.formula) {
    return {
      species: null,
      error: { index: 0, message: 'Add a chemical formula before the charge.' },
    };
  }
  if (charge.formula === 'e') {
    if (charge.charge !== -1) {
      return { species: null, error: { index: 0, message: 'An electron must be written as e-.' } };
    }
    return {
      species: { coefficient, formula: 'e', charge: -1, state: null, atoms: {} },
      error: null,
    };
  }
  const compound = parseCompound(charge.formula);
  if (!compound.compound || compound.error) {
    return { species: null, error: compound.error };
  }
  return {
    species: {
      coefficient,
      formula: compound.compound.formula,
      charge: charge.charge,
      state,
      atoms: compound.compound.atoms,
    },
    error: null,
  };
}

function parseSide(
  input: string,
  offset: number,
): {
  species: IonicSpecies[];
  error: FormulaError | null;
} {
  const parts = input.split(/\s+\+\s+/).map((part) => part.trim());
  if (parts.some((part) => !part)) {
    return {
      species: [],
      error: { index: offset, message: 'Add a species on both sides of each + separator.' },
    };
  }
  const species: IonicSpecies[] = [];
  let searchFrom = 0;
  for (const part of parts) {
    const parsed = parseIonicSpecies(part);
    if (!parsed.species || parsed.error) {
      return {
        species: [],
        error: {
          index: offset + input.indexOf(part, searchFrom) + (parsed.error?.index ?? 0),
          message: parsed.error?.message ?? 'This ionic species could not be recognised.',
        },
      };
    }
    species.push(parsed.species);
    searchFrom = input.indexOf(part, searchFrom) + part.length;
  }
  return { species, error: null };
}

function totals(species: IonicSpecies[]): Record<string, number> {
  const result: Record<string, number> = {};
  species.forEach((item) => {
    Object.entries(item.atoms).forEach(([symbol, count]) => {
      result[symbol] = (result[symbol] ?? 0) + count * item.coefficient;
    });
  });
  return result;
}

function chargeTotal(species: IonicSpecies[]): number {
  return species.reduce((total, item) => total + item.charge * item.coefficient, 0);
}

function cancelSpectators(equation: IonicEquation): {
  spectators: IonicSpecies[];
  netEquation: IonicEquation;
} {
  const leftRemaining = equation.left.map((item) => ({ ...item }));
  const rightRemaining = equation.right.map((item) => ({ ...item }));
  const spectators: IonicSpecies[] = [];
  leftRemaining.forEach((left) => {
    const right = rightRemaining.find((item) => speciesKey(item) === speciesKey(left));
    if (!right) return;
    const cancelled = Math.min(left.coefficient, right.coefficient);
    if (cancelled < 1) return;
    spectators.push({ ...left, coefficient: cancelled });
    left.coefficient -= cancelled;
    right.coefficient -= cancelled;
  });
  return {
    spectators,
    netEquation: {
      left: leftRemaining.filter((item) => item.coefficient > 0),
      right: rightRemaining.filter((item) => item.coefficient > 0),
    },
  };
}

export function analyseIonicEquation(input: string): IonicParseResult {
  const trimmed = input.trim();
  if (!trimmed) return { analysis: null, error: null };
  const arrowMatch = trimmed.match(/->|→/);
  if (!arrowMatch || arrowMatch.index === undefined) {
    return {
      analysis: null,
      error: { index: trimmed.length, message: 'Add -> between reactants and products.' },
    };
  }
  const arrowIndex = arrowMatch.index;
  const leftText = trimmed.slice(0, arrowIndex).trim();
  const rightText = trimmed.slice(arrowIndex + arrowMatch[0].length).trim();
  if (!leftText || !rightText) {
    return {
      analysis: null,
      error: { index: arrowIndex, message: 'Add species on both sides of the reaction arrow.' },
    };
  }
  const left = parseSide(leftText, 0);
  if (left.error) return { analysis: null, error: left.error };
  const rightOffset = arrowIndex + arrowMatch[0].length;
  const right = parseSide(rightText, rightOffset);
  if (right.error) return { analysis: null, error: right.error };
  const equation = { left: left.species, right: right.species };
  const leftAtoms = totals(equation.left);
  const rightAtoms = totals(equation.right);
  const symbols = [...new Set([...Object.keys(leftAtoms), ...Object.keys(rightAtoms)])];
  const atomBalance = symbols.map((symbol) => ({
    symbol,
    left: leftAtoms[symbol] ?? 0,
    right: rightAtoms[symbol] ?? 0,
    balanced: (leftAtoms[symbol] ?? 0) === (rightAtoms[symbol] ?? 0),
  }));
  const cancellation = cancelSpectators(equation);
  const leftCharge = chargeTotal(equation.left);
  const rightCharge = chargeTotal(equation.right);
  return {
    analysis: {
      equation,
      atomBalance,
      leftCharge,
      rightCharge,
      atomsBalanced: atomBalance.length > 0 && atomBalance.every((item) => item.balanced),
      chargeBalanced: leftCharge === rightCharge,
      spectatorSpecies: cancellation.spectators,
      netEquation: cancellation.netEquation,
    },
    error: null,
  };
}

export function ionicSpeciesToText(species: IonicSpecies): string {
  const coefficient = species.coefficient > 1 ? species.coefficient : '';
  const magnitude = Math.abs(species.charge);
  const charge = species.charge
    ? `${magnitude === 1 ? '' : `^${magnitude}`}${species.charge > 0 ? '+' : '-'}`
    : '';
  return `${coefficient}${species.formula}${charge}${species.state ? `(${species.state})` : ''}`;
}

export function ionicEquationToText(equation: IonicEquation): string {
  const side = (items: IonicSpecies[]) => items.map(ionicSpeciesToText).join(' + ');
  return `${side(equation.left)} -> ${side(equation.right)}`;
}
